import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import MeanChart from './MeanChart';
import measurementService from '../../../../services/measurementService';
import patientService from '../../../../services/patientService';
import { toast } from 'react-toastify';
import EmailModal from '../../../../components/EmailModal/EmailModal';
import { sendReportEmail } from '../../../../utils/reportEmailSend';
import { downloadBlob, printElement, toCsv } from '../../../../utils/reportExportUtils';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import MeasurementChartHeader from '../../ChartHeader/MeasurementChartHeader';
import {
  DEFAULT_MEASUREMENT_QUERY,
  isFromToIncomplete,
  isMeasurementQueryDirty,
} from '../../../../utils/queryPeriodFilters';
import { useChartContainerWidth } from '../../../../hooks/useChartContainerWidth';
import { formatPhoneForDisplay } from '../../../../utils/phoneUtils';
import {
  extractWallClockDateYmd,
  formatMeasurementTableDate,
  formatMeasurementTableTime,
  getMeasurementWallClockDateYmd,
  getMeasurementWallClockTimeHms,
} from '../../../../utils/measurementUtils';
import '../../MeasurementShell.css';
import './DailyTrendChart.css';

const DailyTrendChart = ({ practiceId: propPracticeId, patientId: propPatientId }) => {
  const params = useParams();
  const practiceId = propPracticeId || params.practiceId;
  const patientId = propPatientId || params.patientId;
  const location = useLocation();

  const [queryType, setQueryType] = useState(DEFAULT_MEASUREMENT_QUERY.queryType);
  const [dateRange, setDateRange] = useState(DEFAULT_MEASUREMENT_QUERY.dateRange);
  const [fromDate, setFromDate] = useState(DEFAULT_MEASUREMENT_QUERY.fromDate);
  const [toDate, setToDate] = useState(DEFAULT_MEASUREMENT_QUERY.toDate);
  const cacheKey = `chart_patient_meta_${practiceId}_${patientId}`;
  const [patientData, setPatientData] = useState(() => {
    try {
      const raw = sessionStorage.getItem(cacheKey);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [queryLoading, setQueryLoading] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const { updateBreadcrumbs } = useBreadcrumb();

  // Fetch patient data on mount
  useEffect(() => {
    if (practiceId && patientId) {
      setQueryType('dateRange');
      setDateRange('Last 30 days');
      setFromDate('');
      setToDate('');
      fetchPatientData();
    }
  }, [practiceId, patientId]);

  // Fetch measurements on initial load / patient change only (filters apply via Query button).
  useEffect(() => {
    if (practiceId && patientId) {
      fetchMeasurements();
    }
  }, [practiceId, patientId]);

  // Breadcrumbs effect
  useEffect(() => {
    if (patientData?.patient && practiceId && patientId) {
      const fromDashboard = location.state?.fromDashboard;
      const rootCrumb = { label: 'Dashboard', path: '/dashboard' };
      const basePath = fromDashboard ? '/dashboard' : '/practices';

      updateBreadcrumbs([
        rootCrumb,
        { label: patientData.patient.practice_name || 'Practice', path: `${basePath}/${practiceId}`, state: { fromDashboard } },
        { label: 'Patients', path: `${basePath}/${practiceId}?tab=patient`, state: { fromDashboard } },
        { 
          label: `${patientData.patient.last_name || ''}, ${patientData.patient.first_name || ''}`.replace(/^, | ,$|(, )+/g, '$1').trim() || 'Patient', 
          path: `/practices/${practiceId}/patients/${patientId}`,
          state: { fromDashboard }
        },
        { label: 'Blood Pressure Chart', path: null, active: true }
      ]);
    }
  }, [patientData, practiceId, patientId, updateBreadcrumbs, location.state]);

  const fetchPatientData = async () => {
    try {
      const response = await patientService.getMeasurementHeaderFast(practiceId, patientId);
      if (response.data.success) {
        setPatientData(response.data.data);
        try {
          sessionStorage.setItem(cacheKey, JSON.stringify(response.data.data));
        } catch {
          // ignore cache write errors
        }
      }
    } catch (error) {
      console.error('Error fetching patient data:', error);
      toast.error('Failed to load patient data');
    }
  };

  const fetchMeasurements = async () => {
    try {
      setQueryLoading(true);
      let fromDateParam, toDateParam;
      const isAllHistory = queryType === 'dateRange' && String(dateRange).toUpperCase() === 'ALL';

      if (queryType === 'dateRange') {
        if (!isAllHistory) {
          const days = parseInt(dateRange.replace(/\D/g, ''), 10) || 30;
          const endDate = new Date();
          const startDate = new Date();
          startDate.setDate(startDate.getDate() - days);
          fromDateParam = startDate.toISOString().split('T')[0];
          toDateParam = endDate.toISOString().split('T')[0];
        }
      } else {
        // Inputs are already YYYY-MM-DD from type="date"
        if (fromDate && toDate) {
          if (fromDate.includes('/')) {
            const [fromMonth, fromDay, fromYear] = fromDate.split('/');
            const [toMonth, toDay, toYear] = toDate.split('/');
            fromDateParam = `${fromYear}-${fromMonth.padStart(2, '0')}-${fromDay.padStart(2, '0')}`;
            toDateParam = `${toYear}-${toMonth.padStart(2, '0')}-${toDay.padStart(2, '0')}`;
          } else {
            fromDateParam = fromDate;
            toDateParam = toDate;
          }
        }
      }

      if (isAllHistory || (fromDateParam && toDateParam)) {
        const response = await measurementService.getBloodPressureByDateRange(
          practiceId,
          patientId,
          isAllHistory ? undefined : fromDateParam,
          isAllHistory ? undefined : toDateParam,
          'date'
        );
        if (response.data?.success) {
          setMeasurements(response.data.data || []);
        }
      }
      setLoading(false);
    } catch (error) {
      console.error('Error fetching measurements:', error);
      if (error.response?.status !== 404) {
        toast.error('Failed to load measurements');
      }
      setMeasurements([]);
      setLoading(false);
    } finally {
      setQueryLoading(false);
    }
  };

  const handleQuery = () => {
    if (isFromToIncomplete(queryType, fromDate, toDate)) {
      toast.warning('Please select both start and end dates');
      return;
    }
    fetchMeasurements();
  };

  const handleReset = () => {
    setQueryType(DEFAULT_MEASUREMENT_QUERY.queryType);
    setDateRange(DEFAULT_MEASUREMENT_QUERY.dateRange);
    setFromDate(DEFAULT_MEASUREMENT_QUERY.fromDate);
    setToDate(DEFAULT_MEASUREMENT_QUERY.toDate);
  };

  const isResetDisabled = queryLoading || !isMeasurementQueryDirty({
    queryType, dateRange, fromDate, toDate, sortBy: DEFAULT_MEASUREMENT_QUERY.sortBy,
  });

  const patientName = patientData?.patient
    ? `${patientData.patient.first_name || ''} ${patientData.patient.last_name || ''}`.trim()
    : 'Loading...';
  const patientDOB = patientData?.patient?.date_of_birth
    ? new Date(patientData.patient.date_of_birth).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
    : 'Loading...';
  const patientVitals = patientData?.patient?.vitals || '';

  // Calculate daily averages for chart - include all measurements (including ForaCare)
  const chartData = useMemo(() => {
    if (!measurements || measurements.length === 0) return { dates: [], sysData: [], diaData: [], measurementDetails: [] };

    // Group measurements by date and store details for tooltips
    const dailyData = {};
    const measurementDetails = []; // Store individual measurements for tooltips

    measurements.forEach(m => {
      const date = getMeasurementWallClockDateYmd(m);
      const time = (getMeasurementWallClockTimeHms(m) || '').slice(0, 5);
      if (!date) return;

      if (!dailyData[date]) {
        dailyData[date] = { sys: [], dia: [], measurements: [] };
      }

      if (m.systolic_pressure != null) dailyData[date].sys.push(parseFloat(m.systolic_pressure));
      if (m.diastolic_pressure != null) dailyData[date].dia.push(parseFloat(m.diastolic_pressure));

      // Store measurement details for tooltip
      if (m.systolic_pressure != null || m.diastolic_pressure != null) {
        dailyData[date].measurements.push({
          date,
          time,
          sys: m.systolic_pressure ? parseFloat(m.systolic_pressure) : null,
          dia: m.diastolic_pressure ? parseFloat(m.diastolic_pressure) : null,
          fullDateTime: m.measure_new_date_time || m.measure_date_time || m.created_at
        });
      }
    });

    // Calculate averages and sort by date
    const sortedDates = Object.keys(dailyData).sort((a, b) => a.localeCompare(b));
    const sysData = sortedDates.map(date => {
      const values = dailyData[date].sys;
      return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
    });
    const diaData = sortedDates.map(date => {
      const values = dailyData[date].dia;
      return values.length > 0 ? values.reduce((a, b) => a + b, 0) / values.length : null;
    });

    // Store measurement details indexed by date
    const detailsByDate = {};
    sortedDates.forEach(date => {
      detailsByDate[date] = dailyData[date].measurements;
    });

    return { dates: sortedDates, sysData, diaData, measurementDetails: detailsByDate };
  }, [measurements]);

  const [chartAreaRef, chartWidth] = useChartContainerWidth(640, 1100);
  const chartHeight = 400;
  const padding = { top: 20, right: 30, bottom: 65, left: 70 };
  const plotWidth = chartWidth - padding.left - padding.right;
  const plotHeight = chartHeight - padding.top - padding.bottom;
  const maxY = 200;
  const minY = 0;

  // Scale functions (depend on chartWidth)
  const scaleX = (index) => {
    if (chartData.dates.length <= 1) return padding.left;
    return padding.left + (index / (chartData.dates.length - 1)) * plotWidth;
  };

  const scaleY = (value) => {
    if (value == null) return null;
    return padding.top + plotHeight - ((value - minY) / (maxY - minY)) * plotHeight;
  };

  // Generate SVG path for line (break segments when dates have no data)
  const generatePath = (data) => {
    let path = '';
    let prevIndex = null;

    data.forEach((value, index) => {
      const y = scaleY(value);
      if (y == null) {
        prevIndex = null;
        return;
      }
      const x = scaleX(index);
      if (prevIndex == null || index !== prevIndex + 1) {
        path += `${path ? ' ' : ''}M ${x} ${y}`;
      } else {
        path += ` L ${x} ${y}`;
      }
      prevIndex = index;
    });

    return path;
  };

  // Format date for x-axis (wall-clock, no TZ shift)
  const formatChartDate = (dateStr) => {
    const ymd = extractWallClockDateYmd(dateStr);
    return ymd ? ymd.replace(/-/g, '/') : '';
  };

  // Format date/time for tooltip
  const formatTooltipDateTime = (dateStr, timeStr) => {
    const datePart = formatMeasurementTableDate(dateStr);
    if (!datePart) return '';
    if (timeStr) {
      const timePart = formatMeasurementTableTime(timeStr);
      return timePart ? `${datePart} ${timePart}` : datePart;
    }
    return datePart;
  };

  // Tooltip state
  const [tooltip, setTooltip] = useState(null);
  const chartSurfaceRef = useRef(null);
  const tooltipPinnedRef = useRef(false);

  useEffect(() => {
    const handleDocumentClick = (e) => {
      if (chartSurfaceRef.current?.contains(e.target)) return;
      tooltipPinnedRef.current = false;
      setTooltip(null);
    };
    document.addEventListener('click', handleDocumentClick);
    return () => document.removeEventListener('click', handleDocumentClick);
  }, []);

  const showPointTooltip = (e, payload) => {
    const rect = chartSurfaceRef.current?.getBoundingClientRect();
    if (!rect) return;
    setTooltip({
      ...payload,
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handlePrint = () => {
    try {
      const el = document.getElementById('dtc-pdf-content');
      printElement(el, 'Blood Pressure Chart');
    } catch (e) {
      toast.error('Print failed');
    }
  };

  const exportBaseName = `BP-Chart-${patientName.replace(/\s+/g, '-')}-${new Date().toISOString().split('T')[0]}`;

  const handleExportPdf = () => {
    if (!chartData.dates.length) {
      toast.warning('No chart data to export');
      return;
    }
    try {
      const doc = new jsPDF({ orientation: 'landscape', unit: 'mm', format: 'letter' });
      const pageW = doc.internal.pageSize.getWidth();
      const chartLeft = 14;
      const chartTop = 36;
      const chartW = pageW - 28;
      const chartH = 118;
      const yMin = 0;
      const yMax = 200;
      const labels = [0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200];
      const xAt = (i) => (chartData.dates.length <= 1
        ? chartLeft + chartW / 2
        : chartLeft + (i / (chartData.dates.length - 1)) * chartW);
      const yAt = (v) => chartTop + chartH - ((v - yMin) / (yMax - yMin)) * chartH;

      doc.setFont('helvetica', 'bold');
      doc.setFontSize(14);
      doc.text('Blood Pressure Chart Report', 14, 14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(9);
      doc.text(`Name: ${patientName || ''}   DOB: ${patientDOB || ''}`, 14, 20);
      doc.text(`Phone: ${formatPhoneForDisplay(patientData?.patient?.phone_number)}   Device: ${patientData?.device_serial || ''}`, 14, 25);
      doc.text(`Generated: ${new Date().toLocaleString()}`, 14, 30);

      // Target zone 100-150 mmHg
      doc.setFillColor(234, 245, 233);
      doc.rect(chartLeft, yAt(150), chartW, yAt(100) - yAt(150), 'F');

      // Chart border and y-grid
      doc.setDrawColor(210, 220, 232);
      doc.rect(chartLeft, chartTop, chartW, chartH);
      doc.setFontSize(7.5);
      labels.forEach((label) => {
        const y = yAt(label);
        doc.setDrawColor(235, 239, 244);
        doc.line(chartLeft, y, chartLeft + chartW, y);
        doc.setTextColor(90, 98, 109);
        doc.text(String(label), chartLeft - 6, y + 1.7, { align: 'right' });
      });

      // X-axis labels (sampled)
      const step = Math.max(1, Math.floor(chartData.dates.length / 8));
      chartData.dates.forEach((date, idx) => {
        if (!(idx % step === 0 || idx === chartData.dates.length - 1)) return;
        const x = xAt(idx);
        doc.setDrawColor(235, 239, 244);
        doc.line(x, chartTop, x, chartTop + chartH);
        doc.setTextColor(90, 98, 109);
        doc.text(formatChartDate(date), x, chartTop + chartH + 6, { angle: 25, align: 'left' });
      });

      const drawSeries = (data, r, g, b) => {
        let started = false;
        doc.setDrawColor(r, g, b);
        doc.setLineWidth(0.9);
        data.forEach((value, idx) => {
          if (value == null) {
            started = false;
            return;
          }
          const x = xAt(idx);
          const y = yAt(value);
          if (started) {
            const prevIdx = idx - 1;
            const prevValue = data[prevIdx];
            if (prevValue != null) {
              doc.line(xAt(prevIdx), yAt(prevValue), x, y);
            }
          }
          started = true;
          doc.setFillColor(255, 255, 255);
          doc.circle(x, y, 1.1, 'FD');
          doc.setDrawColor(r, g, b);
          doc.circle(x, y, 1.1);
        });
      };
      drawSeries(chartData.sysData, 229, 62, 62);
      drawSeries(chartData.diaData, 49, 130, 206);

      // Legend
      doc.setFontSize(8);
      doc.setTextColor(33, 37, 41);
      doc.setDrawColor(229, 62, 62);
      doc.setLineWidth(1.2);
      doc.line(chartLeft, chartTop + chartH + 13, chartLeft + 8, chartTop + chartH + 13);
      doc.text('Systolic', chartLeft + 10, chartTop + chartH + 14);
      doc.setDrawColor(49, 130, 206);
      doc.line(chartLeft + 32, chartTop + chartH + 13, chartLeft + 40, chartTop + chartH + 13);
      doc.text('Diastolic', chartLeft + 42, chartTop + chartH + 14);
      doc.setFillColor(234, 245, 233);
      doc.rect(chartLeft + 66, chartTop + chartH + 10, 8, 4, 'F');
      doc.text('Target Zone (100-150)', chartLeft + 76, chartTop + chartH + 14);

      // Mean chart on page 2 (data-driven bar chart by period)
      const normalize = (value) => String(value || '').toLowerCase().replace(/[\s_-]/g, '');
      const PERIOD_ORDER = ['Wake-Up', 'Morning', 'Noon', 'Afternoon', 'Evening'];
      const mapPeriod = (m) => {
        const raw = m.period_name || m.period || m.measure_note || m.note || '';
        const key = normalize(raw);
        if (key.includes('wake')) return 'Wake-Up';
        if (key.includes('morning')) return 'Morning';
        if (key.includes('noon') || key.includes('lunch')) return 'Noon';
        if (key.includes('afternoon')) return 'Afternoon';
        if (key.includes('evening') || key.includes('night')) return 'Evening';
        return '';
      };

      const periodMeans = PERIOD_ORDER.map((name) => {
        const rows = measurements.filter((m) => mapPeriod(m) === name);
        const sys = rows
          .map((m) => Number(m.systolic_pressure))
          .filter((n) => !Number.isNaN(n));
        const dia = rows
          .map((m) => Number(m.diastolic_pressure))
          .filter((n) => !Number.isNaN(n));
        const mean = (arr) => (arr.length ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
        return { name, sys: mean(sys), dia: mean(dia) };
      });

      doc.addPage();
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('Mean Chart (By Measurement Period)', 14, 14);
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8.5);
      doc.text(`Name: ${patientName || ''}   DOB: ${patientDOB || ''}`, 14, 20);

      const meanTop = 28;
      const meanH = 95;
      const meanYMax = Math.max(
        200,
        Math.ceil(
          Math.max(
            ...periodMeans.flatMap((p) => [p.sys || 0, p.dia || 0]),
            0
          ) / 20
        ) * 20
      );
      const meanY = (v) => meanTop + meanH - ((v - 0) / (meanYMax - 0)) * meanH;
      const band = chartW / PERIOD_ORDER.length;
      const cx = (idx) => chartLeft + band * idx + band / 2;
      const barW = Math.min(20, Math.max(12, band / 3));

      doc.setDrawColor(210, 220, 232);
      doc.rect(chartLeft, meanTop, chartW, meanH);
      for (let t = 0; t <= meanYMax; t += 20) {
        const y = meanY(t);
        doc.setDrawColor(235, 239, 244);
        doc.line(chartLeft, y, chartLeft + chartW, y);
        doc.setTextColor(90, 98, 109);
        doc.setFontSize(7.5);
        doc.text(String(t), chartLeft - 6, y + 1.7, { align: 'right' });
      }
      PERIOD_ORDER.forEach((name, idx) => {
        const x = cx(idx);
        doc.setDrawColor(235, 239, 244);
        doc.line(x, meanTop, x, meanTop + meanH);
        doc.setTextColor(51, 51, 51);
        doc.setFontSize(8);
        doc.text(name, x, meanTop + meanH + 5, { align: 'center' });
      });

      periodMeans.forEach((p, idx) => {
        const x = cx(idx);
        if (p.sys != null) {
          const y = meanY(p.sys);
          doc.setFillColor(229, 62, 62);
          doc.rect(x - barW - 2, y, barW, meanTop + meanH - y, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(7.5);
          doc.text(String(Math.round(p.sys)), x - barW / 2 - 2, y + 4, { align: 'center' });
        }
        if (p.dia != null) {
          const y = meanY(p.dia);
          doc.setFillColor(49, 130, 206);
          doc.rect(x + 2, y, barW, meanTop + meanH - y, 'F');
          doc.setTextColor(255, 255, 255);
          doc.setFontSize(7.5);
          doc.text(String(Math.round(p.dia)), x + barW / 2 + 2, y + 4, { align: 'center' });
        }
      });

      doc.setTextColor(33, 37, 41);
      doc.setFontSize(8);
      doc.setDrawColor(229, 62, 62);
      doc.setLineWidth(1.2);
      doc.line(chartLeft, meanTop + meanH + 11, chartLeft + 8, meanTop + meanH + 11);
      doc.text('Systolic', chartLeft + 10, meanTop + meanH + 12);
      doc.setDrawColor(49, 130, 206);
      doc.line(chartLeft + 32, meanTop + meanH + 11, chartLeft + 40, meanTop + meanH + 11);
      doc.text('Diastolic', chartLeft + 42, meanTop + meanH + 12);

      const detailRows = [];
      chartData.dates.forEach((date, idx) => {
        const readings = chartData.measurementDetails[date] || [];
        if (!readings.length) {
          detailRows.push([
            formatChartDate(date),
            '',
            '',
            '',
            chartData.sysData[idx] == null ? '' : Math.round(chartData.sysData[idx]),
            chartData.diaData[idx] == null ? '' : Math.round(chartData.diaData[idx]),
          ]);
          return;
        }
        readings.forEach((reading) => {
          detailRows.push([
            formatChartDate(date),
            reading.time || '',
            reading.sys == null ? '' : Math.round(reading.sys),
            reading.dia == null ? '' : Math.round(reading.dia),
            chartData.sysData[idx] == null ? '' : Math.round(chartData.sysData[idx]),
            chartData.diaData[idx] == null ? '' : Math.round(chartData.diaData[idx]),
          ]);
        });
      });

      autoTable(doc, {
        startY: meanTop + meanH + 16,
        head: [['Date', 'Time', 'Systolic', 'Diastolic', 'Daily Avg Sys', 'Daily Avg Dia']],
        body: detailRows,
        theme: 'grid',
        styles: { fontSize: 7, cellPadding: 1.7, halign: 'center', valign: 'middle' },
        headStyles: { fillColor: [46, 74, 120], textColor: [255, 255, 255], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [250, 250, 249] },
        margin: { left: 10, right: 10 },
      });

      doc.save(`${exportBaseName}.pdf`);
      toast.success('Exported to PDF');
    } catch (error) {
      console.error('Error exporting chart PDF:', error);
      toast.error('Export failed');
    }
  };

  const handleExportCsv = () => {
    if (!chartData.dates.length) {
      toast.warning('No chart data to export');
      return;
    }
    const rows = [['Date', 'Time', 'Systolic (mmHg)', 'Diastolic (mmHg)', 'Daily Avg Systolic', 'Daily Avg Diastolic']];
    chartData.dates.forEach((date, idx) => {
      const readings = chartData.measurementDetails[date] || [];
      if (!readings.length) {
        rows.push([formatChartDate(date), '', '', '', chartData.sysData[idx] == null ? '' : Math.round(chartData.sysData[idx]), chartData.diaData[idx] == null ? '' : Math.round(chartData.diaData[idx])]);
        return;
      }
      readings.forEach((reading) => {
        rows.push([
          formatChartDate(date),
          reading.time || '',
          reading.sys == null ? '' : Math.round(reading.sys),
          reading.dia == null ? '' : Math.round(reading.dia),
          chartData.sysData[idx] == null ? '' : Math.round(chartData.sysData[idx]),
          chartData.diaData[idx] == null ? '' : Math.round(chartData.diaData[idx])
        ]);
      });
    });
    downloadBlob(new Blob([toCsv(rows)], { type: 'text/csv;charset=utf-8;' }), `${exportBaseName}.csv`);
    toast.success('Exported to CSV');
  };

  return (
    <div className="daily-trend-chart-page">
      <MeasurementChartHeader
        practiceId={practiceId}
        patientId={patientId}
        vitals={patientVitals}
        activeVital="blood-pressure"
        patientName={patientName}
        patientDOB={patientDOB}
        queryType={queryType}
        setQueryType={setQueryType}
        dateRange={dateRange}
        setDateRange={setDateRange}
        fromDate={fromDate}
        setFromDate={setFromDate}
        toDate={toDate}
        setToDate={setToDate}
        onQuery={handleQuery}
        onReset={handleReset}
        queryDisabled={queryLoading}
        resetDisabled={isResetDisabled}
        onPrint={handlePrint}
        onExportPdf={handleExportPdf}
        onExportCsv={handleExportCsv}
        onEmail={() => setShowEmailModal(true)}
        hasData={measurements.length > 0}
      />

      {/* Daily Trend Chart Section - scrollable */}
      <div className="dtc-charts-scroll" id="dtc-pdf-content">
        <div className="bp-section">
          <div className="bp-section-title-row">
            <h2 className="bp-section-title">Daily Trend Chart</h2>
          </div>
          <div className="bp-card">
            <div className="bp-card-body">
          <div className="dtc-chart-container">
            <div className="dtc-chart-head">
              <div className="dtc-chart-title">Daily Trend Chart of Blood Pressure</div>
              <div className="dtc-chart-legend">
                <div className="dtc-legend-item">
                  <div className="dtc-legend-line dtc-legend-systolic"></div>
                  <span>Systolic Pressure</span>
                </div>
                <div className="dtc-legend-item">
                  <div className="dtc-legend-line dtc-legend-diastolic"></div>
                  <span>Diastolic Pressure</span>
                </div>
              </div>
            </div>
            <div className="dtc-chart-area" ref={chartAreaRef}>
              {loading || queryLoading ? (
                <div className="dtc-chart-status">Loading chart data...</div>
              ) : chartData.dates.length === 0 ? (
                <div className="dtc-chart-status">No data available for the selected period</div>
              ) : (
                <div className="dtc-chart-surface" ref={chartSurfaceRef}>
                  <svg
                    viewBox={`0 0 ${chartWidth} ${chartHeight}`}
                    width={chartWidth}
                    height={chartHeight}
                    preserveAspectRatio="xMidYMid meet"
                    className="dtc-chart-svg"
                    style={{ pointerEvents: 'bounding-box' }}
                  >
                    {/* Y-axis Label */}
                    <text
                      x={15}
                      y={padding.top + plotHeight / 2}
                      textAnchor="middle"
                      dominantBaseline="middle"
                      fill="#333"
                      fontSize="14"
                      fontWeight="600"
                      transform={`rotate(-90, 15, ${padding.top + plotHeight / 2})`}
                    >
                      Blood Pressure mmHg
                    </text>

                    {/* Grid background and outer border */}
                    <rect x={padding.left} y={padding.top} width={plotWidth} height={plotHeight} fill="none" stroke="#e0e0e0" strokeWidth="1" />

                    {/* Horizontal Grid lines and Y-axis text */}
                    {[0, 20, 40, 60, 80, 100, 120, 140, 160, 180, 200].map(value => {
                      const y = scaleY(value);
                      if (y == null) return null;
                      return (
                        <g key={`y-${value}`}>
                          {/* Grid line */}
                          <line
                            x1={padding.left}
                            y1={y}
                            x2={chartWidth - padding.right}
                            y2={y}
                            stroke="#efefef"
                            strokeWidth="1"
                          />
                          {/* Y-axis Text */}
                          <text
                            x={padding.left - 10}
                            y={y}
                            textAnchor="end"
                            dominantBaseline="middle"
                            fill="#666"
                            fontSize="12"
                          >
                            {value}
                          </text>
                        </g>
                      );
                    })}

                    {/* Target Zone */}
                    {scaleY(150) != null && scaleY(100) != null && (
                      <g>
                        <rect
                          x={padding.left}
                          y={scaleY(150)}
                          width={plotWidth}
                          height={scaleY(100) - scaleY(150)}
                          fill="#eaf5e9"
                        />
                        <text
                          x={padding.left + plotWidth / 2}
                          y={scaleY(125)}
                          textAnchor="middle"
                          dominantBaseline="middle"
                          fill="#2e7d32"
                          fontSize="13"
                          fontWeight="bold"
                        >
                          Target Zone
                        </text>
                      </g>
                    )}

                    {/* Vertical grid lines for dates and X-axis text */}
                    {chartData.dates.map((date, index) => {
                      const x = scaleX(index);
                      return (
                        <g key={`x-${date}`}>
                          <line
                            x1={x}
                            y1={padding.top}
                            x2={x}
                            y2={chartHeight - padding.bottom}
                            stroke="#e0e0e0"
                            strokeWidth="1"
                            strokeDasharray="none"
                          />
                          {/* X-axis Text */}
                          {(index % Math.max(1, Math.floor(chartData.dates.length / 10)) === 0 || index === chartData.dates.length - 1) && (
                            <text
                              x={x}
                              y={chartHeight - padding.bottom + 15}
                              textAnchor="end"
                              dominantBaseline="middle"
                              fill="#666"
                              fontSize="11"
                              transform={`rotate(-45, ${x}, ${chartHeight - padding.bottom + 15})`}
                            >
                              {formatChartDate(date)}
                            </text>
                          )}
                        </g>
                      );
                    })}

                    {/* Systolic line (red) */}
                    {chartData.sysData.some(v => v != null) && (
                      <path
                        d={generatePath(chartData.sysData)}
                        fill="none"
                        stroke="#e53e3e"
                        strokeWidth="3"
                      />
                    )}
                    
                    {/* Diastolic line (blue) */}
                    {chartData.diaData.some(v => v != null) && (
                      <path
                        d={generatePath(chartData.diaData)}
                        fill="none"
                        stroke="#3182ce"
                        strokeWidth="3"
                      />
                    )}

                    {/* Data points with hover tooltips */}
                    {chartData.dates.map((date, index) => {
                      const sysValue = chartData.sysData[index];
                      const diaValue = chartData.diaData[index];
                      const x = scaleX(index);
                      const measurements = chartData.measurementDetails[date] || [];
                      const firstMeasurement = measurements[0] || {};

                      if (sysValue == null && diaValue == null) return null;

                      const handlePointer = (e) => {
                        showPointTooltip(e, {
                          date,
                          sysValue,
                          diaValue,
                          time: firstMeasurement.time || '',
                          measurements,
                        });
                      };

                      const handleMouseLeave = () => {
                        if (!tooltipPinnedRef.current) setTooltip(null);
                      };

                      const handleClick = (e) => {
                        e.stopPropagation();
                        tooltipPinnedRef.current = true;
                        handlePointer(e);
                      };

                      return (
                        <g key={`points-${date}`}>
                          <line
                            x1={x}
                            y1={padding.top}
                            x2={x}
                            y2={chartHeight - padding.bottom}
                            stroke="transparent"
                            strokeWidth="24"
                            style={{ cursor: 'pointer' }}
                            onMouseEnter={handlePointer}
                            onMouseLeave={handleMouseLeave}
                            onClick={handleClick}
                          />
                          {sysValue != null && (
                            <circle
                              cx={x}
                              cy={scaleY(sysValue)}
                              r="7"
                              fill="#ffffff"
                              stroke="#e53e3e"
                              strokeWidth="3"
                              style={{ cursor: 'pointer' }}
                              onMouseEnter={handlePointer}
                              onMouseLeave={handleMouseLeave}
                              onClick={handleClick}
                            />
                          )}
                          {diaValue != null && (
                            <circle
                              cx={x}
                              cy={scaleY(diaValue)}
                              r="7"
                              fill="#ffffff"
                              stroke="#3182ce"
                              strokeWidth="3"
                              style={{ cursor: 'pointer' }}
                              onMouseEnter={handlePointer}
                              onMouseLeave={handleMouseLeave}
                              onClick={handleClick}
                            />
                          )}
                        </g>
                      );
                    })}
                  </svg>
                  
                  <div style={{ textAlign: 'center', marginTop: '10px', color: '#666', fontSize: '13px', fontWeight: '500' }}>
                    Time of Day (6 AM - 10 PM)
                  </div>

                  {/* Tooltip */}
                  {tooltip && (
                    <div
                      className="dtc-tooltip"
                      style={{
                        left: `${tooltip.x}px`,
                        top: `${Math.max(8, tooltip.y - 12)}px`,
                        transform: 'translate(-50%, -100%)',
                      }}
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="dtc-tooltip-content">
                        <div className="dtc-tooltip-date">
                          {formatChartDate(tooltip.date)}
                          {tooltip.measurements?.length === 1 && tooltip.time
                            ? ` · ${formatTooltipDateTime(tooltip.date, tooltip.time).split(' ').slice(1).join(' ')}`
                            : ''}
                        </div>
                        {(tooltip.measurements?.length ?? 0) > 1 && (
                          <div className="dtc-tooltip-average-label">Daily average</div>
                        )}
                        {tooltip.sysValue != null && (
                          <div className="dtc-tooltip-item">
                            <span className="dtc-tooltip-label">Systolic</span>
                            <span className="dtc-tooltip-value dtc-tooltip-value-sys">
                              {Math.round(tooltip.sysValue)} mmHg
                            </span>
                          </div>
                        )}
                        {tooltip.diaValue != null && (
                          <div className="dtc-tooltip-item">
                            <span className="dtc-tooltip-label">Diastolic</span>
                            <span className="dtc-tooltip-value dtc-tooltip-value-dia">
                              {Math.round(tooltip.diaValue)} mmHg
                            </span>
                          </div>
                        )}
                        {tooltip.measurements?.length > 1 && (
                          <div className="dtc-tooltip-readings">
                            <div className="dtc-tooltip-readings-title">
                              Readings ({tooltip.measurements.length})
                            </div>
                            {tooltip.measurements.map((m, idx) => (
                              <div key={`${m.date}-${m.time}-${idx}`} className="dtc-tooltip-reading-row">
                                <span className="dtc-tooltip-reading-time">
                                  {m.time
                                    ? formatTooltipDateTime(m.date, m.time).split(' ').slice(1).join(' ')
                                    : '—'}
                                </span>
                                <span className="dtc-tooltip-reading-values">
                                  {m.sys != null ? `${Math.round(m.sys)}` : '—'}
                                  /
                                  {m.dia != null ? `${Math.round(m.dia)}` : '—'}
                                  {' mmHg'}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
            </div>
          </div>
        </div>

        {/* Mean Chart Section */}
        <div className="bp-section dtc-mean-chart-wrapper">
          <div className="bp-section-title-row">
            <h2 className="bp-section-title">Mean Chart</h2>
          </div>
          <div className="bp-card">
            <div className="bp-card-body">
              <MeanChart practiceId={practiceId} patientId={patientId} embedded />
            </div>
          </div>
        </div>
      </div>{/* end dtc-charts-scroll */}

      {/* Email Modal */}
      <EmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        onSend={async (emailData) => {
          try {
            const res = await sendReportEmail(emailData, { event_id: 9 });
            if (!res.success) throw new Error(res.error || res.message);
            toast.success('Email queued successfully');
            setShowEmailModal(false);
          } catch (e) {
            toast.error(e?.message || 'Failed to send email');
          }
        }}
        defaultSubject={`Blood Pressure Chart Report - ${new Date().toLocaleDateString()}`}
        defaultContent={`Blood Pressure Chart Report\nPatient: ${patientName} (DOB: ${patientDOB})\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${measurements.length}`}
      />
    </div>
  );
};

export default DailyTrendChart;

